"use client";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ISchedule,
  ISlot,
  PRESENCE_STATUS_ACCEPTED,
  PRESENCE_STATUS_PENDING,
  PRESENCE_STATUS_REJECTED,
  PRESENCE_TYPE_IN_PERSON,
  PRESENCE_TYPE_REMOTE,
  PresenceStatus,
  PresenceType
} from "@gettactic/api";
import { api } from "@/lib/api/api";
import { ButtonGroup } from "@gettactic/components/src/components/ButtonGroup/ButtonGroup";
import { ButtonInGroup } from "@gettactic/components/src/components/ButtonGroup/ButtonInGroup";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@gettactic/components/src/primitives/Select";
import { cn } from "@gettactic/helpers";
import { toast } from "react-hot-toast";
import { CampusIcon } from "@cmp-src/icons/TacticIcons/CampusIcon";
import { CameraIcon } from "@cmp-src/icons/TacticIcons/CameraIcon";
import { CrossIcon } from "@cmp-src/icons/TacticIcons/CrossIcon";
import { MinusIcon } from "@cmp-src/icons/TacticIcons/MinusIcon";

export type Props = {
  schedule: ISchedule;
  userSlot: ISlot;
  remoteEnabled?: boolean;
};

type PresenceValue = PresenceType | PresenceStatus;

function getValue(
  type: PresenceType | null,
  status: PresenceStatus
): PresenceValue {
  if (status === PRESENCE_STATUS_ACCEPTED) {
    return type ?? PRESENCE_STATUS_ACCEPTED;
  }
  return status;
}

const BUTTON_IN_GROUP_CLASS_NAME = "min-w-[89px] border-y-0 font-normal";
const ICON_SIZE_CLASS = "h-4 w-4";

export const RoomActions = ({
  schedule,
  userSlot,
  remoteEnabled = false
}: Props) => {
  const [presenceStatus, setPresenceStatus] = useState<PresenceStatus>(
    userSlot.presence_status
  );
  const [presenceType, setPresenceType] = useState<PresenceType | null>(
    userSlot.presence_type
  );
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const disabled = !userSlot?.capabilities?.can_respond_presence;
  const queryClient = useQueryClient();

  // Use refs to track current state values for rollback in case of error
  const presenceStatusRef = useRef<PresenceStatus>(userSlot.presence_status);
  const presenceTypeRef = useRef<PresenceType | null>(userSlot.presence_type);

  // Helper to update both state and refs atomically
  const updatePresence = useCallback(
    (status: PresenceStatus, type: PresenceType | null) => {
      setPresenceStatus(status);
      setPresenceType(type);
      presenceStatusRef.current = status;
      presenceTypeRef.current = type;
    },
    []
  );

  // Sync from props when they change
  useEffect(() => {
    updatePresence(userSlot.presence_status, userSlot.presence_type);
  }, [userSlot.presence_status, userSlot.presence_type, updatePresence]);

  const currentValue = getValue(presenceType, presenceStatus);

  const onApprovalPresence = useCallback(
    async (
      status: PresenceStatus,
      presenceType: PresenceType | null,
      prevStatus: PresenceStatus,
      prevType: PresenceType | null
    ): Promise<void> => {
      setIsEditing(true);
      try {
        await api.client.schedules.userPresence(
          schedule.id,
          userSlot.user.id,
          status,
          presenceType ?? undefined
        );
        await queryClient.invalidateQueries({
          queryKey: ["schedules"]
        });
        await queryClient.invalidateQueries({
          queryKey: ["myAgenda"],
          exact: false
        });
        await queryClient.invalidateQueries({
          queryKey: ["schedule", schedule.id]
        });
        await queryClient.invalidateQueries({
          queryKey: ["tableOverview"]
        });
      } catch (error) {
        toast.error("Failed to update presence status");
        updatePresence(prevStatus, prevType);
      } finally {
        setIsEditing(false);
      }
    },
    [schedule.id, userSlot.id, queryClient, updatePresence]
  );

  const handleYes = useCallback(() => {
    const prevStatus = presenceStatusRef.current;
    const prevType = presenceTypeRef.current;
    updatePresence(PRESENCE_STATUS_ACCEPTED, PRESENCE_TYPE_IN_PERSON);
    onApprovalPresence(
      PRESENCE_STATUS_ACCEPTED,
      PRESENCE_TYPE_IN_PERSON,
      prevStatus,
      prevType
    );
  }, [onApprovalPresence, updatePresence]);

  const handleNo = useCallback(() => {
    const prevStatus = presenceStatusRef.current;
    const prevType = presenceTypeRef.current;
    updatePresence(PRESENCE_STATUS_REJECTED, null);
    onApprovalPresence(PRESENCE_STATUS_REJECTED, null, prevStatus, prevType);
  }, [onApprovalPresence, updatePresence]);

  const handleMaybe = useCallback(() => {
    const prevStatus = presenceStatusRef.current;
    const prevType = presenceTypeRef.current;
    updatePresence(PRESENCE_STATUS_PENDING, null);
    onApprovalPresence(PRESENCE_STATUS_PENDING, null, prevStatus, prevType);
  }, [onApprovalPresence, updatePresence]);

  const handleSelectChange = useCallback(
    (value: string) => {
      const prevStatus = presenceStatusRef.current;
      const prevType = presenceTypeRef.current;
      if (value === PRESENCE_TYPE_IN_PERSON || value === PRESENCE_TYPE_REMOTE) {
        updatePresence(PRESENCE_STATUS_ACCEPTED, value as PresenceType);
        onApprovalPresence(
          PRESENCE_STATUS_ACCEPTED,
          value as PresenceType,
          prevStatus,
          prevType
        );
      } else {
        updatePresence(value as PresenceStatus, null);
        onApprovalPresence(value as PresenceStatus, null, prevStatus, prevType);
      }
    },
    [onApprovalPresence, updatePresence]
  );

  const hasResponse =
    presenceStatus !== PRESENCE_STATUS_PENDING || presenceType !== null;

  const selectOptions = useMemo(
    () =>
      [
        {
          value: PRESENCE_TYPE_REMOTE,
          label: "Remote",
          disabled: !remoteEnabled,
          icon: <CameraIcon className={ICON_SIZE_CLASS} />
        },
        {
          value: PRESENCE_TYPE_IN_PERSON,
          label: "In room",
          icon: <CampusIcon className={ICON_SIZE_CLASS} />
        },
        {
          value: PRESENCE_STATUS_PENDING,
          label: "Maybe",
          icon: <MinusIcon className={ICON_SIZE_CLASS} />
        },
        {
          value: PRESENCE_STATUS_REJECTED,
          label: "Decline",
          icon: <CrossIcon className={ICON_SIZE_CLASS} />
        }
      ].filter((option) => !option.disabled),
    [remoteEnabled]
  );

  if (!hasResponse) {
    return (
      <ControlWrapper>
        <ButtonGroup className="rounded-xl border-1" disabled={disabled}>
          <ButtonInGroup
            className={BUTTON_IN_GROUP_CLASS_NAME}
            onClick={handleYes}
            disabled={disabled}
          >
            Yes
          </ButtonInGroup>
          <ButtonInGroup
            className={BUTTON_IN_GROUP_CLASS_NAME}
            onClick={handleNo}
            disabled={disabled}
          >
            No
          </ButtonInGroup>
          <ButtonInGroup
            className={BUTTON_IN_GROUP_CLASS_NAME}
            onClick={handleMaybe}
            disabled={disabled}
          >
            Maybe
          </ButtonInGroup>
        </ButtonGroup>
      </ControlWrapper>
    );
  }

  return (
    <div className="flex flex-row gap-2">
      <ControlWrapper>
        <Select
          value={currentValue}
          onValueChange={handleSelectChange}
          disabled={disabled}
        >
          <SelectTrigger
            className={cn(
              "flex items-center justify-stretch gap-1 ring-1 ring-neutral-50 ",
              presenceType ? "w-[127px]" : ""
            )}
          >
            {/* Uncomment if we want to show the icon */}
            {/* {selectOptions.find((option) => option.value === currentValue)?.icon} */}
            <SelectValue
              className="flex items-center justify-center text-neutral-1000 font-inter"
              placeholder="Choose presence type"
            >
              {presenceType === PRESENCE_TYPE_IN_PERSON ||
              (presenceStatus === PRESENCE_STATUS_ACCEPTED && !presenceType)
                ? "Yes"
                : selectOptions.find((option) => option.value === currentValue)
                    ?.label}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {selectOptions.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                className={cn(
                  "relative flex w-full cursor-default select-none items-center rounded-md bg-background px-1 py-1.5 text-sm outline-none",
                  "hover:bg-neutral-50 focus:bg-neutral-50 focus:text-accent-foreground",
                  "data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                )}
              >
                <div
                  className="flex items-center font-inter font-regular"
                  style={{ fontSize: "14px" }}
                >
                  <span
                    className={cn(
                      "mr-[10px] flex items-center justify-center",
                      ICON_SIZE_CLASS
                    )}
                  >
                    {option.icon}
                  </span>
                  {option.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ControlWrapper>
    </div>
  );
};

const ControlWrapper = ({ children }: { children: React.ReactNode }) => {
  return <div className="flex flex-col gap-2">{children}</div>;
};
